/**
 * Composants Dashboard de Base Réutilisables
 * Phase 3.2 - Architecture modulaire optimisée
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Avatar,
  Grid
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  Home as HomeIcon,
  Star as StarIcon,
  CalendarMonth as CalendarIcon,
  EventBusy as EventBusyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Favorite as FavoriteIcon,
  FlightTakeoff as FlightIcon
} from '@mui/icons-material';

import { 
  formatCurrency, 
  formatPercentage, 
  formatNumber,
  STATUS_COLORS,
  METRIC_ICONS 
} from '../../constants/dashboardConstants';

// Mapping des icônes
const ICON_COMPONENTS = {
  AttachMoney: MoneyIcon,
  Schedule: ScheduleIcon,
  Event: EventIcon,
  Home: HomeIcon,
  CalendarMonth: CalendarIcon,
  EventBusy: EventBusyIcon,
  TrendingUp: TrendingUpIcon,
  Star: StarIcon,
  CheckCircle: CheckCircleIcon,
  Favorite: FavoriteIcon,
  FlightTakeoff: FlightIcon
};

/**
 * Carte de métrique réutilisable
 */
export const MetricCard = ({ 
  title, 
  value, 
  subValue, 
  icon, 
  color = 'primary', 
  trend = null,
  loading = false,
  onRefresh = null,
  format = 'number',
  size = 'medium'
}) => {
  const IconComponent = ICON_COMPONENTS[icon] || MoneyIcon;
  
  const formatValue = (val) => {
    switch (format) {
      case 'currency':
        return formatCurrency(val);
      case 'percentage':
        return formatPercentage(val);
      case 'number':
      default:
        return formatNumber(val);
    }
  };

  const getTrendIcon = () => {
    if (trend > 0) return <TrendingUpIcon fontSize="small" />;
    if (trend < 0) return <TrendingDownIcon fontSize="small" />;
    return <RemoveIcon fontSize="small" />;
  };

  const getTrendColor = () => {
    if (trend > 0) return 'success';
    if (trend < 0) return 'error';
    return 'default';
  };

  const cardHeight = size === 'small' ? 120 : size === 'large' ? 200 : 160;

  return (
    <Card sx={{ height: cardHeight, position: 'relative' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header avec icône et action refresh */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Avatar 
            sx={{ 
              bgcolor: `${color}.main`, 
              width: size === 'small' ? 32 : 40, 
              height: size === 'small' ? 32 : 40 
            }}
          >
            <IconComponent fontSize={size === 'small' ? 'small' : 'medium'} />
          </Avatar>
          
          <Box display="flex" alignItems="center" gap={1}>
            {trend !== null && (
              <Chip 
                label={`${trend > 0 ? '+' : ''}${trend}%`}
                color={getTrendColor()}
                size="small"
                icon={getTrendIcon()}
              />
            )}
            
            {onRefresh && (
              <Tooltip title="Actualiser">
                <IconButton 
                  size="small" 
                  onClick={onRefresh}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {/* Valeur principale */}
        <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography 
                variant={size === 'small' ? 'h5' : size === 'large' ? 'h3' : 'h4'} 
                component="div" 
                fontWeight="bold" 
                color={`${color}.main`}
                sx={{ lineHeight: 1.2 }}
              >
                {formatValue(value)}
              </Typography>
              
              <Typography 
                variant={size === 'small' ? 'caption' : 'body2'} 
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {title}
              </Typography>
              
              {subValue && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {subValue}
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Grille de métriques
 */
export const MetricsGrid = ({ 
  metrics = {}, 
  metricConfig = {}, 
  loading = false, 
  onRefresh = null,
  gridCols = { xs: 1, sm: 2, md: 4 }
}) => {
  return (
    <Grid container spacing={3}>
      {Object.entries(metrics).map(([key, value]) => {
        const config = metricConfig[key] || {};
        
        return (
          <Grid item xs={gridCols.xs} sm={gridCols.sm} md={gridCols.md} key={key}>
            <MetricCard
              title={config.label || key}
              value={value}
              icon={config.icon || METRIC_ICONS[key] || 'AttachMoney'}
              color={config.color || 'primary'}
              format={config.format || 'number'}
              trend={config.trend}
              loading={loading}
              onRefresh={onRefresh}
              size={config.size || 'medium'}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

/**
 * Widget de container réutilisable
 */
export const DashboardWidget = ({
  title,
  children,
  actions = null,
  loading = false,
  error = null,
  onRefresh = null,
  height = 'auto',
  padding = 3
}) => {
  return (
    <Card sx={{ height, mb: 3 }}>
      {/* Header du widget */}
      {(title || actions || onRefresh) && (
        <Box 
          sx={{ 
            p: 2, 
            borderBottom: 1, 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {title && (
            <Typography variant="h6" component="h2">
              {title}
            </Typography>
          )}
          
          <Box display="flex" alignItems="center" gap={1}>
            {actions}
            {onRefresh && (
              <Tooltip title="Actualiser">
                <IconButton 
                  size="small" 
                  onClick={onRefresh}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
      
      {/* Contenu du widget */}
      <CardContent sx={{ p: padding }}>
        {error ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            minHeight={100}
            color="error.main"
          >
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : loading ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            minHeight={100}
          >
            <CircularProgress />
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Indicateur de statut coloré
 */
export const StatusIndicator = ({ 
  status, 
  label = null, 
  size = 'medium',
  variant = 'dot' 
}) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.info;
  
  if (variant === 'chip') {
    return (
      <Chip
        label={label || status}
        size={size}
        sx={{
          bgcolor: color,
          color: 'white',
          '& .MuiChip-label': {
            color: 'white'
          }
        }}
      />
    );
  }
  
  // Variant dot
  const dotSize = size === 'small' ? 8 : size === 'large' ? 16 : 12;
  
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          bgcolor: color
        }}
      />
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Composant de tendance avec icône
 */
export const TrendIndicator = ({ value, showIcon = true, showValue = true }) => {
  if (value === null || value === undefined) return null;
  
  const isPositive = value > 0;
  const isNegative = value < 0;
  const color = isPositive ? 'success' : isNegative ? 'error' : 'text.secondary';
  
  return (
    <Box display="flex" alignItems="center" gap={0.5} color={color}>
      {showIcon && (
        <>
          {isPositive && <TrendingUpIcon fontSize="small" />}
          {isNegative && <TrendingDownIcon fontSize="small" />}
          {!isPositive && !isNegative && <RemoveIcon fontSize="small" />}
        </>
      )}
      {showValue && (
        <Typography variant="body2" color="inherit">
          {isPositive ? '+' : ''}{value}%
        </Typography>
      )}
    </Box>
  );
};

/**
 * Section d'en-tête de dashboard
 */
export const DashboardHeader = ({ 
  title, 
  subtitle, 
  actions = null,
  breadcrumbs = null 
}) => {
  return (
    <Box mb={4}>
      {breadcrumbs}
      
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        
        {actions && (
          <Box display="flex" gap={1} alignItems="center">
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
};
